<script>
  import { positions, symbols, portfolioValue, formatCurrency, formatCrypto } from '../../lib/store.js';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';
  
  let priceUpdates = {}; // Track price updates for animation
  let previousPrices = {};
  
  // Calculate USD value for each position - fully reactive to both positions and symbols changes
  $: enrichedPositions = (() => {
    // Create a new array based on current positions and symbols
    const result = $positions.map(position => {
      let usdPrice = 0;
      let usdValue = 0;
      let priceSymbol = null;
      
      // If the currency itself is USD or stablecoin, value is the balance
      if (position.baseCurrency === 'USD' || position.baseCurrency === 'USDT' || position.baseCurrency === 'USDC') {
        usdPrice = 1;
        usdValue = position.totalBalance;
      } else {
        // Find the USD trading pair for this currency
        const base = position.baseCurrency?.toUpperCase();
        
        // First try direct USD pair
        let symbolData = $symbols.find(s => s.symbol === `${base}/USD`);
        priceSymbol = `${base}/USD`;
        
        // If no USD pair, try USDT
        if (!symbolData) {
          symbolData = $symbols.find(s => s.symbol === `${base}/USDT`);
          priceSymbol = `${base}/USDT`;
        }
        
        // If no USDT pair, try USDC
        if (!symbolData) {
          symbolData = $symbols.find(s => s.symbol === `${base}/USDC`);
          priceSymbol = `${base}/USDC`;
        }
        
        // If still no direct pair and not BTC, try through BTC
        if (!symbolData && position.baseCurrency !== 'BTC') {
          const btcPair = $symbols.find(s => s.symbol === `${base}/BTC`);
          const btcUsdPair = $symbols.find(s => 
            s.symbol === 'BTC/USD' || s.symbol === 'BTC/USDT'
          );
          
          if (btcPair && btcUsdPair) {
            usdPrice = (btcPair.lastPrice || 0) * (btcUsdPair.lastPrice || 0);
            priceSymbol = `${base}/BTC → USD`;
          }
        } else if (symbolData) {
          usdPrice = symbolData.lastPrice || 0;
        }
        
        usdValue = position.totalBalance * usdPrice;
      }
      
      // Track price changes for animation
      const priceKey = position.baseCurrency;
      if (previousPrices[priceKey] !== undefined && 
          Math.abs(previousPrices[priceKey] - usdPrice) > 0.01 && 
          usdPrice !== 1) { // Don't animate stable coins
        priceUpdates[priceKey] = true;
        setTimeout(() => {
          priceUpdates = { ...priceUpdates, [priceKey]: false };
        }, 1000);
      }
      previousPrices[priceKey] = usdPrice;
      
      return {
        ...position,
        usdPrice,
        usdValue,
        priceSymbol,
        percentOfPortfolio: $portfolioValue > 0 ? (usdValue / $portfolioValue) * 100 : 0,
        isUpdating: priceUpdates[priceKey] || false
      };
    });
    
    // Sort by USD value
    return result.sort((a, b) => b.usdValue - a.usdValue);
  })();
  
  function getIconInfo(currency) {
    return getCryptoIconOrLetters(currency, currency);
  }
  
  function getGradient(currency) {
    return getCryptoGradient(currency);
  }
</script>

<div class="portfolio">
  <div class="portfolio-header">
    <h2 class="portfolio-title">Portfolio</h2>
    <div class="portfolio-total">
      <span class="portfolio-total-label">Total Value</span>
      <span class="portfolio-total-value">{formatCurrency($portfolioValue)}</span>
    </div>
  </div>
  
  <div class="portfolio-holdings">
    <div class="portfolio-holdings-header">
      <span>Asset</span>
      <span>Balance</span>
      <span>USD Value</span>
      <span>Allocation</span>
    </div>
    
    <div class="portfolio-holdings-list">
      {#each enrichedPositions as position (position.baseCurrency)}
        {#if position.totalBalance > 0}
          <div class="portfolio-holding">
            <div class="portfolio-asset">
              {#if getIconInfo(position.baseCurrency).type === 'icon'}
                <div class="portfolio-asset-icon icon-symbol" style="background: {getGradient(position.baseCurrency)}">
                  {getIconInfo(position.baseCurrency).value}
                </div>
              {:else}
                <div class="portfolio-asset-icon" style="background: {getGradient(position.baseCurrency)}">
                  {getIconInfo(position.baseCurrency).value}
                </div>
              {/if}
              <div class="portfolio-asset-info">
                <div class="portfolio-asset-symbol">{position.baseCurrency}</div>
                <div class="portfolio-asset-price" class:price-updated={position.isUpdating}>
                  {formatCurrency(position.usdPrice)}
                </div>
              </div>
            </div>
            
            <div class="portfolio-balance">
              <div class="portfolio-balance-crypto">{formatCrypto(position.totalBalance)}</div>
              <div class="portfolio-balance-detail">
                {#if position.onHold > 0}
                  <span class="portfolio-balance-on-hold">🔒 {formatCrypto(position.onHold)}</span>
                {/if}
              </div>
            </div>
            
            <div class="portfolio-usd-value" class:value-updated={position.isUpdating}>
              {formatCurrency(position.usdValue)}
            </div>
            
            <div class="portfolio-allocation">
              <div class="portfolio-allocation-bar">
                <div 
                  class="portfolio-allocation-fill" 
                  style="width: {position.percentOfPortfolio}%"
                ></div>
              </div>
              <span class="portfolio-allocation-percent">
                {position.percentOfPortfolio.toFixed(1)}%
              </span>
            </div>
          </div>
        {/if}
      {/each}
      
      {#if enrichedPositions.filter(p => p.totalBalance > 0).length === 0}
        <div class="portfolio-empty">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2" opacity="0.3"/>
            <path d="M24 14v10M24 28v2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <p class="portfolio-empty-title">No holdings yet</p>
          <span class="portfolio-empty-description">Start trading to build your portfolio</span>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  @import './Portfolio.css';
</style>